import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getPromotions } from '../data/promotionsData';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 40px;
  text-align: center;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(500px, 1fr));
  gap: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PromotionCard = styled.div`
  position: relative;
  height: 400px;
  border-radius: 18px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }
`;

const BannerImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(${props => props.$image});
  background-size: cover;
  background-position: center;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.7));
  }
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 30px;
  color: white;
`;

const PromotionTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 12px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const ShortDescription = styled.p`
  font-size: 16px;
  line-height: 1.5;
  opacity: 0.95;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
  font-size: 18px;
`;

export default function Promotions() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  
  const lang = i18n.language?.startsWith('zh') ? 'zh' : 
               i18n.language?.startsWith('en') ? 'en' : 'ru';
  
  const promotions = getPromotions(lang);

  const handlePromotionClick = (slug) => {
    navigate(`/promotions/${slug}`);
  };

  return (
    <Container>
      <Title>{t('promotions.title', 'Акции')}</Title>
      
      {promotions.length === 0 ? (
        <EmptyState>{t('promotions.empty', 'Нет активных акций')}</EmptyState>
      ) : (
        <Grid>
          {promotions.map((promotion) => (
            <PromotionCard
              key={promotion.id}
              onClick={() => handlePromotionClick(promotion.slug)}
            >
              <BannerImage $image={promotion.bannerImage} />
              <Content>
                <PromotionTitle>{promotion.title}</PromotionTitle>
                {promotion.shortDescription && (
                  <ShortDescription>{promotion.shortDescription}</ShortDescription>
                )}
              </Content>
            </PromotionCard>
          ))}
        </Grid>
      )}
    </Container>
  );
}
